import React, { useEffect, useRef, useState } from "react";
import * as Fa from "react-icons/fa";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { fetchData, postData } from "../../../Services/GlobalService";
import { GetDateFormat } from "../../../Services/GlobalFunction/GetDateFormat";
import {
  downloadExcel,
  downloadExcelDateFormat,
} from "../../../Services/GlobalFunction/DownLoadExcel";
import Loader from "../../../Components/Loader";
import { Tooltip } from "primereact/tooltip";
import { MultiSelect } from "primereact/multiselect";

/* ============================================================
   TYPES
============================================================ */
export type SegmentType = "B2C" | "HDSSD" | "Industrial DB";

const CHANNEL_TYPE_MAP: Record<SegmentType, string> = {
  B2C: "B2C",
  HDSSD: "HDSSD",
  "Industrial DB": "IDB",
};

export interface ConstructOption {
  label: string;
  value: number; // ConstructSeq  (d.Seq from FetchingConstructData)
}

export interface ParameterOption {
  label: string;
  value: number; // ParamSeq  (from GetParameterByConstructSeqId)
}

const TEMPLATE_COLUMNS = ["DB Code", "YearMonth(YYYYMM)", "Corrected Data"];

const FORMAT_LIST = [
  { label: "xlsx", value: "xlsx" },
  { label: "csv", value: "csv" },
];

/* ============================================================
   COMPONENT
============================================================ */
const CorrectedActualUpload: React.FC = () => {
  const UserId = localStorage.getItem("UserId")?.toString();
  const getformat = GetDateFormat();

  /* ── Controller / Endpoint config ── */
  const BaseUrl = "CorrectedActualsUpload";
  const controllerNameConstruct = "DBScoreCard/LBMBrandAndBrandMapping";
  const controllerNameParam = "CommonDropdown";
  const Endpoint = {
    GetConstructList: "FetchingConstructData",
    GetParameterList: "DropDownList",
    DownloadData: "AcheivedSummaryData",
    DownloadDatadetls: "AcheiveDetailsData",
    Upload: "AcheivedSummaryUpload",
  };

  const fileUploadRef = useRef<FileUpload>(null);

  /* ── State ── */
  const [segment, setSegment] = useState<SegmentType>("B2C");

  const [constructList, setConstructList] = useState<ConstructOption[]>([]);
  const [distList, setDistList] = useState<ConstructOption[]>([]);
  const [selectedDist, setSelectedDist] = useState<ConstructOption[]>([]);
  const [selectedConstruct, setSelectedConstruct] =
    useState<ConstructOption | null>(null);
  const [constructLoading, setConstructLoading] = useState<boolean>(false);
  const [downloadDataLoadingdts, setDownloadDataLoadingdts] =
    useState<boolean>(false);

  const [parameterList, setParameterList] = useState<ParameterOption[]>([]);
  const [selectedParameter, setSelectedParameter] =
    useState<ParameterOption | null>(null);
  const [parameterLoading, setParameterLoading] = useState<boolean>(false);

  const [templateFormat, setTemplateFormat] = useState<"xlsx" | "csv">("xlsx");

  const [loader, setLoader] = useState<boolean>(false);
  const [downloadDataLoading, setDownloadDataLoading] =
    useState<boolean>(false);

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});

  const [errorxlsbtn, setErrorxlsbtn] = useState<boolean>(false);
  const [errorxlsData, setErrorxlsData] = useState<any[]>([]);
  const [monthYearOption, setMonthYearOption] = useState<any[]>([]);
  const [selectedMntyr, setSelectedMntyr] = useState<any[]>([]);

  useEffect(() => {
    setSelectedConstruct(null);
    setConstructList([]);
    setSelectedParameter(null);
    setParameterList([]);
    loadConstructList();
    loadParameterList();
  }, [segment]);

  useEffect(() => {
    let populateDist = async () => {
      try {
        const PopulateDistData = await fetchData(
          "CommonDropdown",
          "DropDownList",
          `UserId=${UserId}&PVCtype=DBSIP_Dist_List&PVCPAYLOAD=${JSON.stringify(
            {
              userid: UserId,
              region: "ALL",
              state: "ALL",
            },
          )}`,
        );
        const updatedDistList = PopulateDistData?.Data?.map((item: any) => ({
          ...item,
          displayLabel: `${item.COMP_CODE} - ${item.COMP_NAME}`,
        }));
        setDistList(updatedDistList ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        // (false);
      }
    };
    const now = new Date();

    const currentYear = now.getFullYear();
    const previousYear = currentYear - 1;

    const generateMonths = (year: any) =>
      Array.from({ length: 12 }, (_, index) => {
        const month = String(index + 1).padStart(2, "0");

        return {
          label: `${year}${month}`,
          value: `${year}${month}`,
        };
      });

    const monthOptions = [
      ...generateMonths(previousYear),
      ...generateMonths(currentYear),
    ];

    setMonthYearOption(monthOptions);
    populateDist();
  }, []);

  const loadConstructList = async () => {
    setConstructLoading(true);
    try {
      const res = await fetchData(
        controllerNameConstruct,
        Endpoint.GetConstructList,
        `ChannelType=${CHANNEL_TYPE_MAP[segment]}`,
      );
      setConstructList(
        (res.Data ?? []).map((d: any) => ({
          label: d.Name,
          value: d.Seq,
        })),
      );
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to load constructs");
    } finally {
      setConstructLoading(false);
    }
  };

  const loadParameterList = async () => {
    setParameterLoading(true);
    try {
      const payload = JSON.stringify({ stream: CHANNEL_TYPE_MAP[segment] });
      const res = await fetchData(
        controllerNameParam,
        Endpoint.GetParameterList,
        `UserId=${UserId}&PVCtype=DBSIP_PARAM_SEQ&PVCPAYLOAD=${encodeURIComponent(payload)}`,
      );
      setParameterList(
        (res.Data ?? []).map((d: any) => ({
          label: d.Name ?? d.ParameterName ?? d.PARAM_NAME ?? "",
          value: d.Seq ?? d.ParameterSeq ?? d.PARAM_SEQ ?? 0,
        })),
      );
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Failed to load parameters");
    } finally {
      setParameterLoading(false);
    }
  };
  const validateForm = () => {
    if (!selectedConstruct) toast.warn("Please select a Construct");
    else if (!selectedParameter) {
      toast.warn("Please select a Parameter");
      return 0;
    } else return 1;
    return 0;
  };

  const handleDownloadTemplate = () => {
    const headers = [
      {
        "DB Code": null,
        "YearMonth(YYYYMM)": null,
        "Corrected Data": null,
      },
    ];
    downloadExcel(
      headers,
      `DB_SIP_Corrected_Actuals_Template_${getformat}.${templateFormat}`,
    );
  };

  const handleDownloadData = async () => {
    if (!validateForm()) return;
    setDownloadDataLoading(true);
    try {
      const res = await fetchData(
        BaseUrl,
        Endpoint.DownloadData,
        `STREAM=${CHANNEL_TYPE_MAP[segment]}&CONSTRACTSEQ=${selectedConstruct}&PARAMSEQ=${selectedParameter}`,
      );
      if (res?.Data?.length > 0) {
        downloadExcelDateFormat(
          res.Data,
          `DBSIP_Actuals_Summary_Data${getformat}.xlsx`,
        );
      } else {
        toast.error("No Data Found");
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Download failed");
    } finally {
      setDownloadDataLoading(false);
    }
  };
  const handleDownloadDatadtls = async () => {
    if (!validateForm()) return;
    setDownloadDataLoadingdts(true);
    try {
      const res = await fetchData(
        BaseUrl,
        Endpoint.DownloadDatadetls,
        `STREAM=${CHANNEL_TYPE_MAP[segment]}&CONSTRACTSEQ=${selectedConstruct}&PARAMSEQ=${selectedParameter}&YEARMONTH=${selectedMntyr}&COMPID_LIST=${selectedDist.map((d: any) => d.COMP_CODE).join(",")}`,
      );
      if (res?.Data?.length > 0) {
        downloadExcelDateFormat(
          res.Data,
          `DBSIP_Actuals_Detail_Data${getformat}.csv`,
        );
      } else {
        toast.warn(
          "No Data Found for the selected Month-Year and Distributors. Please adjust your filters and try again.",
        );
      }
    } catch (ex: any) {
      toast.error(
        ex?.response?.data?.Message ??
          "It seems dataset is too large, kindly try with lesser selection.",
      );
    } finally {
      setDownloadDataLoadingdts(false);
    }
  };

  const parseUploadedFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
            raw: false,
            defval: "",
            blankrows: false,
          });

          const filteredData = jsonData.filter((row) =>
            Object.values(row).some((v) => v !== ""),
          );

          if (!filteredData.length) {
            reject(new Error("The file has no valid data."));
            return;
          }

          /* Validate required columns */
          const actualHeaders = Object.keys(filteredData[0]).map((h) =>
            h.trim(),
          );
          const missingHeaders = TEMPLATE_COLUMNS.filter(
            (col) => !actualHeaders.includes(col),
          );

          if (missingHeaders.length > 0) {
            reject(
              new Error(
                `Missing required columns: ${missingHeaders.join(", ")}`,
              ),
            );
            return;
          }

          resolve(filteredData);
        } catch {
          reject(
            new Error(
              "Failed to parse the file. Please ensure it is a valid Excel or CSV file.",
            ),
          );
        }
      };

      reader.onerror = () => reject(new Error("Failed to read the file."));
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileUpload = async (event: any) => {
    setSubmitted(true);

    if (!validateForm()) {
      fileUploadRef.current?.clear();
      return;
    }

    const file: File = event.files?.[0];
    if (!file) return;

    try {
      setLoader(true);

      /* ─ parse file ── */
      let rows: any[];
      try {
        rows = await parseUploadedFile(file);
      } catch (parseErr: any) {
        toast.warn(parseErr.message ?? "File parse error");
        fileUploadRef.current?.clear();
        setErrorxlsbtn(false);
        return;
      }

      /* ── map rows to achvSummaries ── */
      const achvSummaries = rows.map((row: any) => {
        const dbCode = String(row["DB Code"] ?? "").trim();
        const yearmonth = String(row["YearMonth(YYYYMM)"] ?? "").trim();

        const achievement = String(row["Corrected Data"] ?? "").trim();

        return {
          dB_CODE: dbCode,
          yearmonth,
          achievement,
        };
      });

      /* ──  build & POST payload ── */
      const payload = {
        stream: CHANNEL_TYPE_MAP[segment],
        useR_ID: UserId ?? "",
        constructseq: selectedConstruct,
        parameterseq: selectedParameter,
        achvSummaries,
      };

      const res = await postData(BaseUrl, Endpoint.Upload, payload);

      if (
        res?.Status === "Success" ||
        res?.Message?.toLowerCase().includes("success")
      ) {
        toast.success(res.Message ?? "Data uploaded successfully.");
        fileUploadRef.current?.clear();
        setErrorxlsbtn(false);
      } else if (res?.Status === "ErrorFR") {
        toast.warn(res.Message);
        fileUploadRef.current?.clear();
        setErrorxlsbtn(false);
      } else if (res?.Status === "Error") {
        toast.warn(res.Message ?? "Upload failed");
        setErrorxlsbtn(true);
        setErrorxlsData(res.Data ?? []);
      } else {
        /* Fallback: treat any other response as success */
        toast.success(res?.Message ?? "Upload failed");
        fileUploadRef.current?.clear();
        setErrorxlsbtn(false);
      }
    } catch (ex: any) {
      toast.error(ex?.response?.data?.Message ?? "Upload failed");
      fileUploadRef.current?.clear();
      setErrorxlsbtn(false);
    } finally {
      setLoader(false);
    }
  };

  if (loader) return <Loader />;

  return (
    <main>
      <div className="col-12 mb-5">
        {/* ── Breadcrumb ── */}
        <div className="col-lg-12 col-md-12 col-sm-12">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mx-0">
              <li className="breadcrumb-item">DB SIP</li>
              <li className="breadcrumb-item active" aria-current="page">
                Corrected Actuals Upload
              </li>
            </ol>
          </nav>
        </div>

        <div className="row">
          <div className="col-lg-6 col-md-6 col-sm-12">
            <h3 className="py-2">Corrected Actuals Upload</h3>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="text-end">
              <div className="d-inline-flex">
                <Dropdown
                  value={templateFormat}
                  onChange={(e) => setTemplateFormat(e.value)}
                  options={FORMAT_LIST}
                  optionLabel="label"
                  style={{
                    width: 100,
                    borderRadius: "0 6px 6px 0",
                    border: "1px solid #ced4da",
                    borderLeft: "none",
                    height: "100%",
                  }}
                />
              </div>
              <button
                type="button"
                className="btn anime-secondary-btn mx-2"
                onClick={handleDownloadTemplate}
              >
                Download Template <Fa.FaDownload className="ms-2" />
              </button>
              <button
                type="button"
                className="btn anime-btn"
                onClick={handleDownloadData}
                disabled={downloadDataLoading}
              >
                {downloadDataLoading ? (
                  <>
                    Downloading...{" "}
                    <span
                      className="spinner-border spinner-border-sm ms-1"
                      role="status"
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <>
                    Summary Data <Fa.FaFileExport className="ms-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <section className="sales-box">
          <div
            className="border rounded p-3 mb-2"
            style={{ backgroundColor: "#f7f7f7" }}
          >
            <div className="row d-flex justify-content-center align-items-center">
              {/* Segment Radio Buttons */}
              <div className="col-lg-4 col-md-12 col-sm-12">
                <div className="d-flex flex-wrap gap-3">
                  {(["B2C", "HDSSD", "Industrial DB"] as SegmentType[]).map(
                    (s) => (
                      <div key={s} className="d-inline-flex align-items-center">
                        <RadioButton
                          inputId={`seg-${s}`}
                          name="segment"
                          value={s}
                          onChange={(e) => setSegment(e.value)}
                          checked={segment === s}
                        />
                        <label
                          htmlFor={`seg-${s}`}
                          className="form-label ms-2 mb-0"
                        >
                          {s}
                        </label>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {/* Construct Dropdown */}
              <div className="col-lg-4 col-md-6 col-sm-12">
                <div className="d-block">
                  <Dropdown
                    value={selectedConstruct}
                    onChange={(e) => {
                      setSelectedConstruct(e.value);
                      setErrors({ ...errors, selectedConstruct: null });
                    }}
                    options={constructList}
                    optionLabel="label"
                    placeholder={
                      constructLoading ? "Loading..." : "Select Construct"
                    }
                    disabled={constructLoading}
                    className={
                      submitted && errors.selectedConstruct
                        ? "p-invalid w-100"
                        : "w-100"
                    }
                  />
                  {submitted && errors.selectedConstruct && (
                    <small className="p-error">
                      {errors.selectedConstruct}
                    </small>
                  )}
                </div>
              </div>

              {/* Parameter Dropdown  */}
              <div className="col-lg-4 col-md-6 col-sm-12">
                <div className="d-block">
                  <Dropdown
                    value={selectedParameter}
                    onChange={(e) => {
                      setSelectedParameter(e.value);
                      setErrors({ ...errors, selectedParameter: null });
                    }}
                    options={parameterList}
                    optionLabel="label"
                    placeholder={
                      parameterLoading ? "Loading..." : "Select Parameter"
                    }
                    disabled={parameterLoading} // ← removed `!selectedConstruct ||`
                    className={
                      submitted && errors.selectedParameter
                        ? "p-invalid w-100"
                        : "w-100"
                    }
                  />
                  {submitted && errors.selectedParameter && (
                    <small className="p-error">
                      {errors.selectedParameter}
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="row d-flex justify-content-end align-items-end">
            <div className="col-lg-7 col-md-7 col-sm-12">
              {/* ── Upload Section ── */}
              <div className="UploadSection clearfix">
                <div className="conText">
                  <Fa.FaInfoCircle className="icon" /> Select an Excel or CSV
                  File to upload
                </div>
                <FileUpload
                  ref={fileUploadRef}
                  className="clearfix nTopMargin"
                  name="demo[]"
                  accept=".xlsx, .xls, .csv"
                  emptyTemplate={
                    <p className="m-0">
                      Drag and drop files to here to upload.
                    </p>
                  }
                  customUpload
                  uploadHandler={handleFileUpload}
                  onClear={() => {
                    setErrorxlsbtn(false);
                    setSubmitted(false);
                  }}
                  onRemove={() => {
                    setErrorxlsbtn(false);
                    setSubmitted(false);
                  }}
                />
                <div className="row d-flex justify-content-center align-items-center mt-2">
                  <div className="col-lg-9 col-md-12 col-sm-12">
                    <small className="text-danger">
                      <i>
                        * Upload corrected achievement data
                      </i>
                    </small>
                  </div>
                  <div className="col-lg-3 col-md-12 col-sm-12">
                    <div className="text-end">
                      <Tooltip target=".custom-target-icon" />
                      <i
                        className="custom-target-icon pi pi-info-circle p-text-secondary"
                        data-pr-tooltip="DB Code, YearMonth(YYYYMM), Corrected Data"
                        style={{ fontSize: "1.2rem", cursor: "pointer" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Illustration — unchanged */}
            <div className="col-lg-5 col-md-5 col-sm-12">
              <div className="text-end">
                <img
                  src={require("../../../Assets/Images/File_Upload.png")}
                  className="File_Upload_Img drop-right"
                  alt="InnoRise"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="sales-box">
          <h6>SKU wise data download</h6>
          <div className="row d-flex justify-content-end align-items-end">
            <div className="col-lg-4 col-md-6 col-sm-12">
              <div className="d-block">
                <Dropdown
                  value={selectedMntyr}
                  onChange={(e) => {
                    setSelectedMntyr(e.value);
                  }}
                  options={monthYearOption}
                  optionLabel="label"
                  placeholder="Select Month Year"
                />
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12">
              <div className="d-block">
                <MultiSelect
                  value={selectedDist}
                  onChange={(e) => {
                    setSelectedDist(e.value);
                  }}
                  options={distList}
                  optionLabel="displayLabel"
                  placeholder="Select Distributor"
                  display="chip"
                  filter
                />
              </div>
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12">
              <div className="text-end">
                <button
                  type="button"
                  className="btn anime-btn"
                  onClick={handleDownloadDatadtls}
                  disabled={downloadDataLoadingdts}
                >
                  {downloadDataLoadingdts ? (
                    <>
                      Downloading...{" "}
                      <span
                        className="spinner-border spinner-border-sm ms-1"
                        role="status"
                        aria-hidden="true"
                      />
                    </>
                  ) : (
                    <>
                      Details Data <Fa.FaFileExport className="ms-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ── Template Columns Info — unchanged ── */}
        {/* <section className="col-4 offset-4">
          <div
            className="border rounded p-3 mt-3 bg-white"
            style={{ background: "#f8f9fa" }}
          >
            <h6>Template Columns:</h6>
            <ul className="mb-0" style={{ color: "#555" }}>
              {TEMPLATE_COLUMNS.map((col) => (
                <li key={col} style={{ listStyle: "disc" }}>
                  {col}
                </li>
              ))}
            </ul>
          </div>
        </section> */}
      </div>
    </main>
  );
};

export default CorrectedActualUpload;
